// @flow
import React, { Component, type Node } from 'react';
import styled from 'styled-components';
import { isNull } from 'lodash';

import { mergeClassNames } from 'utils/functions';
import Icon from 'components/Icon';

type AlertProps = {
    /** determines the color of the alert */
    flavor: string,
    /** will be appended to the classes */
    className: string,
    /** function run on close */
    closeCallback?: null,
    /** what will be rendered inside the alert */
    children: Node,
    /** icon for flavor. differentiate with icon name or null to keep one from rendering */
    icon?: string,
};

const StyledAlert = styled.div`
    div:last-child {
        p:last-child {
            margin-bottom: 0;
        }
    }
`;

const iconFlavors = {
    // the i/info is default
    danger: 'exclamation-circle',
    success: 'check-circle',
    warning: 'exclamation-circle',
};

const iconColors = {
    danger: 'danger',
    info: 'info',
    light: 'secondary',
    primary: 'primary',
    secondary: 'reset',
    success: 'success',
    warning: 'warning',
};

/** A component for an alert. */
export class Alert extends Component<AlertProps> {
    static defaultProps = {
        flavor: 'info',
        className: '',
        closeCallback: null,
    };

    render() {
        let {
            flavor,
            closeCallback,
            children,
            icon,
            className,
            ...domProps
        } = this.props;

        const iconColor = iconColors[flavor] || 'white';

        className = mergeClassNames('d-flex mt-1 mb-3', className);

        if (!isNull(icon)) {
            icon = icon || iconFlavors[flavor] || 'info-circle';
        }

        return (
            <StyledAlert
                className={`border-${flavor} ${className} border`}
                {...domProps}
            >
                {icon ? (
                    <div className={`bg-${flavor} px-3 d-flex`}>
                        <Icon
                            name={icon}
                            className={`align-self-center fa-lg text-${iconColor}`}
                        />
                    </div>
                ) : (
                    <div className={`badge-${flavor} px-1`} />
                )}
                <div className={'p-3 d-flex flex-grow-1 bg-white'}>
                    <div className='flex-grow-1'>{children}</div>
                    {closeCallback && (
                        <button
                            onClick={closeCallback}
                            type='button'
                            className='close align-self-start ml-3'
                            aria-label='Close'
                        >
                            <span aria-hidden='true'>&times;</span>
                        </button>
                    )}
                </div>
            </StyledAlert>
        );
    }
}

export default Alert;
